import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Phone, 
  Mail, 
  Calendar, 
  ArrowLeft,
  X,
  MessageSquare,
  Globe,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity,
  RefreshCw,
  Search,
  Edit3,
  Building2,
  UserCheck
} from 'lucide-react';
import { getModeratorContacts, getAllApplications } from '../../services/moderatorService';
import { useAuthStore } from '../../stores/authStore';
import { JobApplication, CompanyContact } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import EditContactModal from './EditContactModal';

const MyContactsPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editContact, setEditContact] = useState<CompanyContact | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Carica contatti e applications in parallelo
      const [moderatorContacts, allApplications] = await Promise.all([
        getModeratorContacts(user?.uid || ''),
        getAllApplications()
      ]);
      
      setContacts(moderatorContacts);
      setApplications(allApplications);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
  };

  const openEditModal = (contact: CompanyContact) => {
    setEditContact(contact);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setEditContact(null);
    setShowEditModal(false);
  };

  const getContactMethodIcon = (method: string) => {
    switch (method) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'phone': return <Phone className="w-4 h-4" />;
      case 'linkedin': return <MessageSquare className="w-4 h-4" />;
      case 'letter': return <FileText className="w-4 h-4" />;
      case 'direct': return <Globe className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  // Funzione per ottenere il nome del candidato da un contatto
  const getCandidateInfo = (contact: CompanyContact) => {
    const application = applications.find(app => app.id === contact.applicationId);
    return {
      name: application?.userName || 'N/A',
      email: application?.userEmail || 'N/A'
    };
  };

  // Calcola statistiche
  const stats = {
    totalContacts: contacts.length,
    positiveResponses: contacts.filter(c => c.response === 'positive').length,
    negativeResponses: contacts.filter(c => c.response === 'negative').length,
    pendingResponses: contacts.filter(c => c.response === 'pending').length,
    noResponses: contacts.filter(c => c.response === 'no-response').length
  };

  // Filtra contatti per ricerca
  const filteredContacts = contacts.filter(contact => {
    if (!searchFilter.trim()) return true;
    
    const candidateInfo = getCandidateInfo(contact);
    const searchLower = searchFilter.toLowerCase();
    
    return candidateInfo.name.toLowerCase().includes(searchLower) ||
           candidateInfo.email.toLowerCase().includes(searchLower) ||
           contact.companyName.toLowerCase().includes(searchLower) ||
           (contact.contactPerson && contact.contactPerson.toLowerCase().includes(searchLower));
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Caricamento Contatti...</h3>
          <p className="text-gray-600">Stiamo preparando i tuoi dati</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Errore</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={loadData}
            className="w-full bg-blue-600 text-white rounded-xl py-3 px-4 font-medium hover:bg-blue-700 transition-colors"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      {/* Header con Welcome e Azioni Rapide */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 lg:gap-3 mb-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-1.5 lg:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg lg:rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
              <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg lg:rounded-xl">
                <FileText className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">I Miei Contatti</h1>
                <p className="text-sm lg:text-base text-gray-600">Database completo delle tue attività di contatto con le aziende</p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-gray-100 text-gray-700 rounded-lg lg:rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm lg:text-base"
            >
              <RefreshCw className={`w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
          </div>
        </div>
      </div>

      {/* Statistiche Principali - Mobile: 2x2, Desktop: 1x4 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Totale Contatti</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.totalContacts}</p>
              <p className="text-xs text-gray-500 mt-1">Effettuati</p>
            </div>
            <div className="p-2 lg:p-3 bg-blue-100 rounded-lg lg:rounded-xl">
              <FileText className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Risposte Positive</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.positiveResponses}</p>
              <p className="text-xs text-gray-500 mt-1">Interessate</p>
            </div>
            <div className="p-2 lg:p-3 bg-green-100 rounded-lg lg:rounded-xl">
              <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">In Attesa</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.pendingResponses}</p>
              <p className="text-xs text-gray-500 mt-1">Da seguire</p>
            </div>
            <div className="p-2 lg:p-3 bg-yellow-100 rounded-lg lg:rounded-xl">
              <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Risposte Negative</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.negativeResponses}</p>
              <p className="text-xs text-gray-500 mt-1">Non interessate</p>
            </div>
            <div className="p-2 lg:p-3 bg-red-100 rounded-lg lg:rounded-xl">
              <XCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Statistica aggiuntiva per mobile */}
      <div className="lg:hidden">
        <div className="bg-white rounded-xl shadow-lg p-4 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Nessuna Risposta</p>
              <p className="text-2xl font-bold text-gray-900">{stats.noResponses}</p>
              <p className="text-xs text-gray-500 mt-1">Silenzio</p>
            </div>
            <div className="p-2 bg-gray-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca per candidato, azienda, persona contattata..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 lg:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-all text-sm lg:text-base"
              />
            </div>
          </div>
          <div className="text-sm lg:text-base text-gray-600">
            {filteredContacts.length} di {contacts.length} contatti
          </div>
        </div>
      </div>

      {/* Lista Contatti */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
            Contatti ({filteredContacts.length})
          </h2>
          <div className="text-sm lg:text-base text-gray-600">
            Ordinate per data (più recenti)
          </div>
        </div>
        
        {/* Desktop: Griglia 2x2 */}
        <div className="hidden lg:block p-4 lg:p-6">
          {filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">
                  {searchFilter ? 'Nessun contatto trovato' : 'Nessun contatto'}
                </p>
                <p className="text-sm">
                  {searchFilter ? 'Prova a modificare i criteri di ricerca' : 'Non hai ancora contatti nel database'}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 lg:gap-6">
              {filteredContacts.map((contact) => {
                const candidateInfo = getCandidateInfo(contact);
                return (
                  <div key={contact.id} className="bg-gray-50 rounded-xl p-4 lg:p-6 border border-gray-200 hover:shadow-lg transition-all duration-200 hover:border-gray-300">
                    {/* Header Card */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <div className="p-2 bg-blue-100 rounded-lg mr-3">
                            <Building2 className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 text-lg">{contact.companyName}</h3>
                            <p className="text-blue-600 font-medium text-sm">{candidateInfo.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 mb-1">
                          <UserCheck className="w-4 h-4 mr-2 text-gray-500" />
                          <span className="font-medium">{candidateInfo.email}</span>
                        </div>
                        {contact.contactPerson && (
                          <p className="text-sm text-gray-500">Contattato: {contact.contactPerson}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(contact)}
                          className="p-2 text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 rounded-lg transition-colors"
                          title="Modifica contatto"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Dettagli Card */}
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">Data</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {contact.contactDate ? format(contact.contactDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                          </p>
                        </div>

                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center gap-2 mb-1">
                            {getContactMethodIcon(contact.contactMethod)}
                            <span className="text-sm font-medium text-gray-700">Metodo</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 capitalize">{contact.contactMethod}</p>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Risposta:</span>
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                          contact.response === 'positive' ? 'bg-green-100 text-green-800 border-green-200' :
                          contact.response === 'negative' ? 'bg-red-100 text-red-800 border-red-200' :
                          contact.response === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                          'bg-gray-100 text-gray-800 border-gray-200'
                        }`}>
                          {contact.response === 'positive' ? <CheckCircle className="w-3 h-3" /> :
                           contact.response === 'negative' ? <XCircle className="w-3 h-3" /> :
                           contact.response === 'pending' ? <Clock className="w-3 h-3" /> :
                           <AlertCircle className="w-3 h-3" />}
                          <span className="text-sm font-medium">
                            {contact.response === 'positive' ? 'Positiva' :
                             contact.response === 'negative' ? 'Negativa' :
                             contact.response === 'pending' ? 'In Attesa' :
                             contact.response === 'no-response' ? 'Nessuna Risposta' : 'N/A'}
                          </span>
                        </span>
                      </div>

                      {/* Follow-up Date */}
                      {contact.followUpDate && (
                        <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-4 h-4 text-yellow-600" />
                            <span className="text-sm font-medium text-yellow-800">Follow-up</span>
                          </div>
                          <p className="text-sm font-semibold text-yellow-900">
                            {format(contact.followUpDate.toDate(), 'dd/MM/yyyy', { locale: it })}
                          </p>
                        </div>
                      )}

                      {/* Notes */}
                      {contact.notes && (
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <span className="text-sm font-medium text-blue-700 block mb-1">Note:</span>
                          <p className="text-sm text-blue-900">{contact.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mobile: Cards */}
        <div className="lg:hidden p-3 lg:p-4 space-y-3 lg:space-y-4">
          {filteredContacts.length === 0 ? (
            <div className="text-center py-6 lg:py-8">
              <div className="text-gray-500">
                <FileText className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-3 lg:mb-4 text-gray-300" />
                <p className="text-base lg:text-lg font-medium">
                  {searchFilter ? 'Nessun contatto trovato' : 'Nessun contatto'}
                </p>
                <p className="text-xs lg:text-sm">
                  {searchFilter ? 'Prova a modificare i criteri di ricerca' : 'Non hai ancora contatti nel database'}
                </p>
              </div>
            </div>
          ) : (
            filteredContacts.map((contact) => {
              const candidateInfo = getCandidateInfo(contact);
              return (
                <div key={contact.id} className="bg-gray-50 rounded-lg lg:rounded-xl p-3 lg:p-4 border border-gray-200 hover:shadow-md transition-shadow">
                  {/* Header Card */}
                  <div className="flex justify-between items-start mb-3 lg:mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-1 lg:mb-2">
                        <div className="p-1 bg-blue-100 rounded mr-2">
                          <Building2 className="w-3 h-3 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 text-base lg:text-lg">{contact.companyName}</h3>
                      </div>
                      <p className="text-xs lg:text-sm text-gray-600 mb-1">{candidateInfo.name}</p>
                      <div className="flex items-center text-xs lg:text-sm text-gray-600">
                        <UserCheck className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
                        <span className="font-medium">{candidateInfo.email}</span>
                      </div>
                      {contact.contactPerson && (
                        <p className="text-xs text-gray-500">Contattato: {contact.contactPerson}</p>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => openEditModal(contact)}
                        className="p-1 text-yellow-600 hover:text-yellow-900"
                        title="Modifica contatto"
                      >
                        <Edit3 className="w-4 h-4 lg:w-5 lg:h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Dettagli Card */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs lg:text-sm text-gray-600">Data Contatto:</span>
                      <span className="text-xs lg:text-sm font-medium text-gray-900">
                        {contact.contactDate ? format(contact.contactDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs lg:text-sm text-gray-600">Metodo:</span>
                      <div className="flex items-center">
                        {getContactMethodIcon(contact.contactMethod)}
                        <span className="ml-1 text-xs lg:text-sm font-medium text-gray-900 capitalize">{contact.contactMethod}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs lg:text-sm text-gray-600">Risposta:</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${
                        contact.response === 'positive' ? 'bg-green-100 text-green-800' :
                        contact.response === 'negative' ? 'bg-red-100 text-red-800' :
                        contact.response === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {contact.response === 'positive' ? 'Positiva' :
                         contact.response === 'negative' ? 'Negativa' :
                         contact.response === 'pending' ? 'In Attesa' :
                         contact.response === 'no-response' ? 'Nessuna Risposta' : 'N/A'}
                      </span>
                    </div>

                    {contact.followUpDate && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs lg:text-sm text-gray-600">Follow Up:</span>
                        <span className="text-xs lg:text-sm font-medium text-gray-900">
                          {format(contact.followUpDate.toDate(), 'dd/MM/yyyy', { locale: it })}
                        </span>
                      </div>
                    )}

                    {contact.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-xs lg:text-sm text-gray-600 block mb-1">Note:</span>
                        <p className="text-xs lg:text-sm text-gray-900 bg-white p-2 rounded border">{contact.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal Modifica Contatto */}
      {showEditModal && editContact && (
        <EditContactModal
          isOpen={showEditModal}
          onClose={closeEditModal}
          contact={editContact}
          onContactUpdated={loadData}
        />
      )}
    </div>
  );
};

export default MyContactsPage; 